import { IMAGES } from 'src/assets'
import i18n from 'src/i18n'
import clsx from 'clsx'
import { UploadBox } from 'src/components/InputMarkdown/components'
import { PostType } from 'src/hook/usePosts'

import { MediaCard } from 'src/components/mediaCard'

import { useState } from 'react'
import { PermissionActions } from 'src/services/posts'
import { filterPermission } from 'src/utils/user'
import { PERMISSIONS_ACTIONS } from 'src/utils/user/constant'
import { useCommunityDetails } from 'src/pages/CommunityDetails/Posts/context/communityDetailsContext'
import { faClose } from '@fortawesome/pro-light-svg-icons/faClose'
import { Icon } from 'src/components'
import { toggleLiveModalEventDispatch } from '@space-lib-events/toggleLiveModal'
import { useDecision } from '@optimizely/react-sdk'
import { AudioPlayer } from 'src/components/audioPlayer'
import { ComponentName } from 'src/utils/datahubDefaultEvent'
import { usePostModal } from '../../hooks'
import { usePostModalUpload } from '../../hooks/usePostModalUpload'
import RemoveMediaButton from '../removeMediaButton/removeMediaButton'
import { PostModalPreviewVideo } from '../postModalPreviewVideo'
import { PostModalActionsProps } from './postModalActions.types'
import { CallActionsPostModal, CustomButton } from './postModalActions.styles'

export const getIsOpenUploadNewState = (
  prevState: boolean,
  uploadBoxMediaType: PostType,
  newMediaType: PostType
): boolean => uploadBoxMediaType !== newMediaType || !prevState

const PostModalActions = ({
  className,
  isOpenUpload = false,
  currentMedia,
  fileUploaded,
  isFileUploaded = false,
  postType,
  setFileUploaded,
  setPostType,
  setMedia,
  setCurrentMedia,
  setIsOpenUpload,
  setUploading,
  onRemoveMedia
}: PostModalActionsProps) => {
  const [{ enabled: enableAudioPost }] = useDecision('com_eng_web_enable_audio_post_consume')
  const { isFullScreen } = usePostModal()
  const { community } = useCommunityDetails()
  const [isLoading, setIsLoading] = useState(false)
  const [uploadBoxMediaType, setUploadBoxMediaType] = useState(PostType.MEDIA)
  const { onDropZoneSuccess } = usePostModalUpload({
    setPostType,
    setMedia,
    setCurrentMedia,
    setIsOpenUpload,
    setFileUploaded
  })

  const canCreateLive = filterPermission({
    specificPermission: PERMISSIONS_ACTIONS.WEBINAR_CREATE,
    permissionsUserList: community.permissions?.actions as PermissionActions[]
  })

  const CallActionsPostModalClassNames = clsx(className, {
    '_border-0': isFullScreen
  })

  const showPreviewFileUploaded = !isFullScreen && isOpenUpload && fileUploaded && isFileUploaded

  const showUploadBox = !isFullScreen && isOpenUpload && !isFileUploaded

  const shouldShowCreateLiveButton = canCreateLive

  const buttonIsActive = isFileUploaded || isLoading

  const defaultMediaButtonClassName =
    '_d-flex _align-items-center _rounded-pill _border _py-2 _px-4 _bg-transparent'
  const getMediaClassName = (newMediaType: PostType) => {
    return clsx(defaultMediaButtonClassName, {
      '_bg-gray-100 _border-white': isOpenUpload && newMediaType === uploadBoxMediaType,
      '_bg-gray-200 _border-white': buttonIsActive && newMediaType === uploadBoxMediaType,
      '_cursor-pointer': !showPreviewFileUploaded && !isLoading,
      disabled: showPreviewFileUploaded || isLoading
    })
  }

  const removeMedia = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    setIsOpenUpload?.(false)
  }

  const handleLoading = (state: boolean) => {
    setIsLoading(state)
    setUploading(state)
  }

  const handleMediaButtonClick = (newMediaType: PostType) => {
    setIsOpenUpload((prev) => getIsOpenUploadNewState(prev, uploadBoxMediaType, newMediaType))

    setUploadBoxMediaType(newMediaType)
  }

  const handleLiveButtonClick = () => {
    toggleLiveModalEventDispatch({ action: 'open' })
  }

  return (
    <>
      <div className="_d-flex _flex-column _w-full">
        {showUploadBox && (
          <div className="_w-full _pt-5">
            <UploadBox
              classNames="_mb-2 _mx-7"
              mediaType={uploadBoxMediaType}
              onLoading={handleLoading}
              onSuccess={onDropZoneSuccess}
            >
              <RemoveMediaButton
                onRemove={removeMedia}
                icon={<Icon icon={faClose} className="_text-2" />}
              />
            </UploadBox>
          </div>
        )}

        {showPreviewFileUploaded && (
          <>
            {PostType.VIDEO === postType && currentMedia && (
              <div className="_w-full _pt-5 _px-8">
                <PostModalPreviewVideo
                  linkVideo={currentMedia}
                  onRemoveMedia={onRemoveMedia}
                  classNameContainer="_w-half"
                />
              </div>
            )}

            {PostType.AUDIO === postType && (
              <div className="_mt-5 _ml-5 _mb-4">
                <AudioPlayer
                  src={currentMedia as string}
                  onClickDeleteButton={onRemoveMedia}
                  componentName={ComponentName.PLAY_ON_POST_CREATION}
                />
              </div>
            )}

            {PostType.IMAGE === postType && (
              <div className="_border-top _border-gray-200 _w-full _pt-5 _d-flex _justify-content-center _align-items-center">
                <MediaCard
                  className="_mx-8 _max-w-full"
                  name={fileUploaded?.name}
                  size={fileUploaded?.size}
                  extension={fileUploaded?.extension}
                  thumbnailUrl={currentMedia}
                  onDelete={onRemoveMedia}
                />
              </div>
            )}
          </>
        )}

        <CallActionsPostModal className={CallActionsPostModalClassNames}>
          <CustomButton
            type="button"
            className={getMediaClassName(PostType.IMAGE)}
            onClick={() => handleMediaButtonClick(PostType.IMAGE)}
            data-test="add-image-button"
            disabled={showPreviewFileUploaded}
          >
            <img
              src={IMAGES.MEDIA}
              alt={i18n.t('input_publish.button_image')}
              className="_mr-2"
              loading="lazy"
            />

            <span className="_text-2 _text-gray-900">{i18n.t('input_publish.button_image')}</span>
          </CustomButton>

          <CustomButton
            type="button"
            className={getMediaClassName(PostType.VIDEO)}
            onClick={() => handleMediaButtonClick(PostType.VIDEO)}
            data-test="add-video-button"
            disabled={showPreviewFileUploaded}
          >
            <img
              src={IMAGES.VIDEO}
              alt={i18n.t('input_publish.button_video')}
              className="_mr-2"
              loading="lazy"
            />

            <span className="_text-2 _text-gray-900">{i18n.t('input_publish.button_video')}</span>
          </CustomButton>

          {enableAudioPost && (
            <CustomButton
              type="button"
              className={getMediaClassName(PostType.AUDIO)}
              onClick={() => handleMediaButtonClick(PostType.AUDIO)}
              data-test="add-audio-button"
              disabled={showPreviewFileUploaded}
            >
              <img
                src={IMAGES.AUDIO}
                alt={i18n.t('input_publish.button_audio')}
                className="_mr-2"
                loading="lazy"
              />
              <span className="_text-2 _text-gray-900">{i18n.t('input_publish.button_audio')}</span>
            </CustomButton>
          )}

          {shouldShowCreateLiveButton && (
            <CustomButton
              type="button"
              onClick={handleLiveButtonClick}
              className={defaultMediaButtonClassName}
              data-test="live-button"
            >
              <img src={IMAGES.LIVE} alt="live" className="_mr-2" loading="lazy" />
              <span className="_text-2 _text-gray-900">{i18n.t('input_publish.button_live')}</span>
            </CustomButton>
          )}
        </CallActionsPostModal>
      </div>
    </>
  )
}

export default PostModalActions
